import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { PrimeNG } from 'primeng/config';
import { SidebarModule } from 'primeng/sidebar';
import { Subscription } from 'rxjs';
import { Role } from './enums/role';
import { AuthService } from './services/auth.service';
import { ThemeService } from './services/theme.service';
import { Parameters } from './utilities/parameters';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SidebarModule,
    ButtonModule,
    AvatarModule,
    TranslatePipe
    ]
})
export class AppComponent implements OnDestroy {
  title: string = "ComfTech";
  menuLinks: MenuItem[] = [];

  userAuthenticatedSubscription: Subscription;
  userDataAvailableSubscription: Subscription;
  userAuthenticated: boolean = false;

  constructor(private themeService: ThemeService, private translateService: TranslateService, private authService: AuthService, private config: PrimeNG, private router: Router) {
    this.themeService.loadSaved();

    this.translateService.addLangs(['it', 'en']);
    this.translateService.setFallbackLang('en');

    const browserLang = this.translateService.getBrowserLang();
    let lang = browserLang === undefined ? 'en' : (browserLang.match(/en|it/) ? browserLang : 'en');
    this.translateService.use(lang);
    this.translateService.get('primeng').subscribe(res => this.config.setTranslation(res));

    this.userAuthenticatedSubscription = this.authService.loginChanged.subscribe(res => {
      if (res) {
        this.userAuthenticated = true;
      }
    });

    this.userDataAvailableSubscription = this.authService.userDataAvailable.subscribe(r => {
      if (r) {
        this.setMenuLinks();
        this.router.navigate([this.getDashboard()]);
      }
    });
  }

  ngOnDestroy() {
    if (this.userAuthenticatedSubscription) {
      this.userAuthenticatedSubscription.unsubscribe();
    }
    if (this.userDataAvailableSubscription) {
      this.userDataAvailableSubscription.unsubscribe();
    }
  }

  public getAvatarCode(): string {
    if (Parameters.loggedUser === undefined || !this.userAuthenticated) {
      return 'U';
    } else if (Parameters.loggedUser.id === '') {
      return 'U';
    } else {
      return `${Parameters.loggedUser.name !== '' ? Parameters.loggedUser.name.charAt(0) : ''}${Parameters.loggedUser.familyName !== '' ? Parameters.loggedUser.familyName.charAt(0) : ''}`;
    }
  }

  public getDisplayName(): string {
    if (Parameters.loggedUser === undefined || !this.userAuthenticated) {
      return 'user';
    } else if (Parameters.loggedUser.id === '') {
      return 'user';
    } else {
      return `${Parameters.loggedUser.name} ${Parameters.loggedUser.familyName}`;
    }
  }

  public login = () => {
    const currentLanguage = this.translateService.currentLang() || this.translateService.fallbackLang() || 'en';
    this.authService.login(currentLanguage);
  }

  public logout = () => {
    this.authService.logout();
  }

  private checkRole(roles: number[]): boolean {
    if (Parameters.loggedUser.id === '') {
      return false;
    }

    if (roles.length == 1) {
      return Parameters.loggedUser.roles.includes(roles[0]);
    } else if (roles.length < 1) {
      return false;
    } else {
      return Parameters.loggedUser.roles.some(r => roles.includes(r));
    }
  }

  private getDashboard(): string {
    if (Parameters.loggedUser.id === '') {
      return '';
    }

    if (this.checkRole([Role.clinician])) {
      return '/dashboard-clinic';
    } else if (this.checkRole([Role.patient])) {
      return '/personal';
    } else if (this.checkRole([Role.researcher])) {
      return '/invalid-role';
    } else if (this.checkRole([Role.superAdmin, Role.admin])) {
      return '/dashboard-admin';
    } else {
      return '/invalid-role';
    }
  }

  private setMenuLinks() {
    this.menuLinks = [];

    if (this.userAuthenticated) {
      this.menuLinks = [
        {
          label: 'Dashboard',
          visible: true,
          items: [
            {
              label: 'admin',
              icon: 'pi-hammer',
              visible: this.checkRole([Role.superAdmin, Role.admin]),
              routerLink: '/dashboard-admin',
            },
            {
              label: 'clinician',
              icon: 'pi-compress',
              visible: this.checkRole([Role.clinician]),
              routerLink: '/dashboard-clinic',
            },
            {
              label: 'personal',
              icon: 'pi-user',
              visible: this.checkRole([Role.patient]),
              routerLink: '/personal',
            },
          ]
        },
        {
          visible: this.checkRole([Role.clinician]),
          items: [
            {
              label: 'patientList',
              icon: 'pi-users',
              visible: true,
              routerLink: '/patients',
            },
            {
              label: 'alerts',
              icon: 'pi-exclamation-triangle',
              visible: true,
              routerLink: '/alerts'
            }
          ]
        },
        {
          label: 'management',
          visible: this.checkRole([Role.superAdmin, Role.admin]),
          items: [
            {
              label: 'organizations',
              icon: 'pi-warehouse',
              visible: this.checkRole([Role.superAdmin]),
              routerLink: '/organizations'
            },
            {
              label: 'departments',
              icon: 'pi-sitemap',
              visible: true,
              routerLink: '/departments'
            },
            {
              label: 'users',
              icon: 'pi-users',
              visible: true,
              routerLink: '/users'
            },
            {
              label: 'activatedServices',
              icon: 'pi pi-wrench',
              visible: this.checkRole([Role.superAdmin]),
              routerLink: '/services'
            },
            {
              label: 'crons',
              icon: 'pi pi-clock',
              visible: true,
              routerLink: '/workers'
            }
          ]
        },
        {
          label: 'patient',
          visible: this.checkRole([Role.patient]),
          items: [
          ]
        },
        {
          label: 'research',
          visible: this.checkRole([Role.researcher]),
          items: [
            {
              label: 'Dataset',
              icon: 'pi-database',
              visible: true
            },
            {
              label: 'Query Builder',
              icon: 'pi-filter',
              visible: true
            }
          ]
        }
      ]
    }
  }
}
